import React from 'react';
import { X } from 'lucide-react';

interface CityFormProps {
  city: {
    name: string;
    slug: string;
    zip_codes: string[] | null;
    description: string;
    county: string;
    latitude: number;
    longitude: number;
  };
  onChange: (field: string, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function CityForm({ city, onChange, onSubmit, onCancel, isSubmitting }: CityFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {city.name ? 'Edit City' : 'Add City'}
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="p-2 text-gray-400 hover:text-gray-500"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          City Name
        </label>
        <input
          type="text"
          value={city.name}
          onChange={(e) => onChange('name', e.target.value)}
          className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          placeholder="e.g., Los Angeles"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Slug
        </label>
        <input
          type="text"
          value={city.slug}
          onChange={(e) => onChange('slug', e.target.value)}
          className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          placeholder="e.g., los-angeles"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          ZIP Codes (comma separated)
        </label>
        <input
          type="text"
          value={city.zip_codes?.join(',') || ''}
          onChange={(e) => onChange('zip_codes', e.target.value.split(',').map(zip => zip.trim()))}
          className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          placeholder="e.g., 90001,90002,90003"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          value={city.description}
          onChange={(e) => onChange('description', e.target.value)}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="Brief description of plumbing services in this city"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          County
        </label>
        <input
          type="text"
          value={city.county}
          onChange={(e) => onChange('county', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          placeholder="e.g., Los Angeles County"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Latitude
          </label>
          <input
            type="number"
            step="any"
            value={city.latitude}
            onChange={(e) => onChange('latitude', parseFloat(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
            placeholder="e.g., 34.0522"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Longitude
          </label>
          <input
            type="number"
            step="any"
            value={city.longitude}
            onChange={(e) => onChange('longitude', parseFloat(e.target.value))}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
            placeholder="e.g., -118.2437"
          />
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
}